<template>
  <div
    class="canvas-block flex flex-col rounded-2 w-full overflow-auto relative"
    :class="{
      'py-12 px-8 border-2 border-dotted': isEditMode,
      'border-primary5': !isBlockSelected,
      'border-info': isBlockSelected,
      'min-h-[250px]': isEmpty,
    }"
    :data-block-id="data?.id"
  >
    <BlockToolbar
      v-if="isEditMode && (isBlockSelected || isEmpty)"
      class="top-0 right-0 left-0"
      :is-saving="isSaving"
      @remove="removeBlock"
      @save="onSaveOverrides"
    />
    <div v-if="isLoading" class="flex flex-col justify-center items-center p-4">
      <NSkeleton v-for="n in 3" :key="n" class="w-full rounded" :class="{ 'mt-3': n > 1 }" :height="200" />
    </div>
    <div v-if="!isLoading && error && !isEditMode" class="flex justify-center items-center p-4 color-error">
      {{ error }}
    </div>
    <template v-if="!isLoading && !error && blockContent?.length">
      <div
        v-for="c in blockContent"
        :key="c.id"
        v-element-visibility="(isVisible: boolean) => onElementVisibility(isVisible, c)"
        class="component-wrapper relative"
        :data-block-component-id="c.id"
        :style="{
          marginBottom: c?.style?.marginBottom || `${globalComponentSpacing}px`,
        }"
      >
        <!-- skipping link for now -->
        <Component :is="componentMap[c.type]" v-bind="getComponentBindings(c)" />
      </div>
    </template>
    <BlockToolbar
      v-if="isEditMode && (isBlockSelected || isEmpty)"
      class="bottom-0 right-0 left-0"
      :is-saving="isSaving"
      @remove="removeBlock"
      @save="onSaveOverrides"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, provide } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import { omit } from 'lodash-es'
import { NSkeleton } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import BlockToolbar from './BlockToolbar.vue'
import { applyOverridesToNode } from './Block.helpers'
import Grid from '@canvas-builder/components/ui/Grid/Grid.vue'
import Carousel from '@canvas-builder/components/ui/Carousel/Carousel.vue'
import ContentGrid from '@canvas-builder/components/ui/ContentGrid/ContentGrid.vue'
import DataCharts from '@canvas-builder/components/ui/DataCharts/DataCharts.vue'
import DynamicDataTable from '@canvas-builder/components/ui/DynamicDataTable/DynamicDataTable.vue'
import Embeddable from '@canvas-builder/components/ui/Embeddable/Embeddable.vue'
import Scribble from '@canvas-builder/components/ui/Scribble/Scribble.vue'
import Text from '@canvas-builder/components/ui/Text/Text.vue'
import Html from '@canvas-builder/components/ui/Html/Html.vue'
import IFrame from '@canvas-builder/components/ui/IFrame/IFrame.vue'
import CollectionPlayer from '@canvas-builder/components/ui/CollectionPlayer/CollectionPlayer.vue'
import Link from '@canvas-builder/components/ui/Link/Link.vue'
import Multimedia from '@canvas-builder/components/ui/Multimedia/Multimedia.vue'
import Timeline from '@canvas-builder/components/ui/Timeline/Timeline.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useCanvasBlocks from '@canvas-builder/composables/useCanvasBlocks'
import type { ComponentTypes, ComponentNode, BlockProps } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve } from '@openapi'
import type { BlockContext } from '@canvas-builder/components/ui/Block/Block.types'
import useToast from '@lib/composables/useToast'
import { findNodeInTreeById } from '@canvas-builder/util/tree'

const props = defineProps<{
  id: ComponentNode['id']
  data?: BlockProps
  style?: Record<string, any>
}>()

const componentMap = {
  Grid,
  Carousel,
  ContentGrid,
  DataCharts,
  DynamicDataTable,
  Embeddable,
  Scribble,
  Text,
  Multimedia,
  Html,
  IFrame,
  Link,
  Timeline,
  CollectionPlayer,
} as Record<ComponentTypes, any>
const { t } = useI18n()
const { toast } = useToast()
const { globalComponentSpacing } = useCanvasTheme()
const {
  onComponentEnter,
  onComponentExit,
  activateNavigationItem,
  deactivateNavigationItem,
  isEditMode,
  removeComponentById,
  updateNodeDataById,
  componentSelectedMode,
} = useCanvas()

provide(
  'usedInBlockCtx',
  computed(() => ({
    usedInBlockId: props.data?.id,
    overrideBlockComponentData: (blockComponentId: ComponentNode['id'], data: Record<string, any>) => {
      if (props.data?.id) {
        updateNodeDataById(props.id, {
          ...props.data,
          overrides: {
            ...props.data?.overrides,
            [blockComponentId]: { node_data: data },
          },
        })
      }
    },
  })) satisfies BlockContext,
)
const { fetchBlock, saveUserBlockOverrides } = useCanvasBlocks()
const isSaving = ref(false)
const isLoading = ref(true)
const error = ref<string | null>(null)
const fetchedBlock = ref<CanvasRetrieve | null>(null)
const rawBlockContent = computed<ComponentNode[]>(() => fetchedBlock.value?.content?.data || [])
const createdInEditMode = isEditMode.value

const blockContent = computed(() => {
  if (!rawBlockContent.value?.length) return []

  return rawBlockContent.value.map((component) => applyOverridesToNode(component, props.data?.overrides))
})

const isBlockSelected = computed(() => {
  if (!componentSelectedMode.value) return false

  return !!findNodeInTreeById(blockContent.value, componentSelectedMode.value.id)
})

const isEmpty = computed(() => {
  return !blockContent.value?.length
})

onMounted(async () => {
  try {
    if (!props.data?.id) {
      throw new Error('Block ID is required')
    }

    fetchedBlock.value = await fetchBlock(props.data.id)
    isLoading.value = false
  } catch (e) {
    error.value = t('canvasUI.canvasBuilder.components.block.errorLoadingBlock')
    console.warn((e as Error)?.message || error.value)
    isLoading.value = false
  }
})

function onElementVisibility(isVisible: boolean, c: ComponentNode) {
  if (isVisible) {
    onComponentEnter.value?.(c, { trackable: true })
    activateNavigationItem(c.id, undefined, !createdInEditMode)
  } else {
    onComponentExit.value?.(c, { trackable: true })
    deactivateNavigationItem(c.id, undefined, !createdInEditMode)
  }
}

function getComponentBindings(component: ComponentNode) {
  const fieldsToOmit = ['type']
  if (!component.children?.length) {
    fieldsToOmit.push('children')
  }

  return {
    ...omit(component, fieldsToOmit),
    style: {
      ...component?.style,
      marginBottom: undefined,
    },
  }
}

function removeBlock() {
  props.id && removeComponentById(props.id)
}

async function onSaveOverrides() {
  if (props.data?.id) {
    isSaving.value = true
    await saveUserBlockOverrides(props.data.id, props.data.overrides ?? {})
      .then(() => {
        toast.success(t('canvasUI.canvasBuilder.components.block.userOverridesSaved'))
      })
      .catch(() => {
        toast.error(t('canvasUI.canvasBuilder.components.block.errorSavingUserOverrides'))
      })
      .finally(() => {
        isSaving.value = false
      })
  }
}
</script>
